import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Wallet, CheckCircle2, AlertTriangle, Info, ArrowUp, ArrowDown, Clock, ArrowUpRight, Users, TrendingUp, TrendingDown, ChevronRight, BarChart3, FileSpreadsheet, FileText, FileDown, Download, Zap } from "lucide-react";
import { api, apiError, money } from "@/lib/api";
import MyPayouts from "@/components/MyPayouts";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { useAuth } from "@/context/AuthContext";

const TABS = [["executados", "Executados"], ["pendentes", "Pendentes"], ["futuros", "Futuros"]];
const KIND_MAP = {
  in: { tipo: "Entrada", status: "Recebido", dir: "in" },
  out: { tipo: "Repasse", status: "Pago", dir: "out" },
  pending: { tipo: "Entrada", status: "A receber", dir: "in" },
  problem: { tipo: "Entrada", status: "Pendência", dir: "in" },
  out_pending: { tipo: "Repasse", status: "A pagar", dir: "out" },
  future: { tipo: "Entrada", status: "Previsto", dir: "in" },
  future_soft: { tipo: "Entrada", status: "Potencial", dir: "in" },
};

const fmtDate = (s) => {
  if (!s) return "";
  const d = String(s).includes("T") ? new Date(s) : new Date(s + "T00:00:00");
  if (isNaN(d.getTime())) return String(s);
  return d.toLocaleDateString("pt-BR", { weekday: "short", day: "2-digit", month: "short", year: "numeric" }).replace(".", "");
};
const shortMoney = (v) => (Math.abs(v) >= 1000 ? `R$ ${(v / 1000).toFixed(1).replace(".", ",")}k` : money(v));
const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

export default function Payments() {
  const navigate = useNavigate();
  const { hasFeature } = useAuth();
  const [fin, setFin] = useState(null);
  const [mus, setMus] = useState(null);
  const [payout, setPayout] = useState(null);
  const [tab, setTab] = useState("executados");
  const [exporting, setExporting] = useState("");

  useEffect(() => {
    const fetchAll = () => {
      api.get("/finance/summary").then((r) => setFin(r.data)).catch(() => {});
      api.get("/finance/musician").then((r) => setMus(r.data)).catch(() => {});
      api.get("/me/payout-info").then((r) => setPayout(r.data)).catch(() => {});
    };
    fetchAll();
    const t = setInterval(fetchAll, 12000);
    return () => clearInterval(t);
  }, []);

  const hasPix = payout && payout.status !== "Não cadastrado";
  const buckets = mus?.buckets || {};
  const totals = mus?.totals || {};
  const recebido = totals.recebido || 0;
  const aReceber = totals.a_receber || 0;
  const repasses = fin?.contractor?.total_pago || 0;
  const saldoLiquido = recebido - repasses;
  const pendingPix = (buckets.problemas || []).filter((x) => (x.reason || "").toLowerCase().includes("pix")).reduce((s, x) => s + (x.cache || 0), 0);

  // Série mensal (últimos 6 meses) a partir dos repasses recebidos (bucket recebido)
  const chart = useMemo(() => {
    const now = new Date();
    const cells = [];
    for (let i = 5; i >= 0; i--) cells.push(new Date(now.getFullYear(), now.getMonth() - i, 1));
    const sums = cells.map(() => 0);
    (buckets.recebido || []).forEach((r) => {
      const raw = r.timeline?.recebido || r.paid_at || r.event_date;
      if (!raw) return;
      const d = String(raw).includes("T") ? new Date(raw) : new Date(raw + "T00:00:00");
      const idx = cells.findIndex((c) => c.getFullYear() === d.getFullYear() && c.getMonth() === d.getMonth());
      if (idx >= 0) sums[idx] += r.cache || 0;
    });
    const max = Math.max(...sums, 1);
    return cells.map((c, i) => ({ label: cap(c.toLocaleDateString("pt-BR", { month: "short" }).replace(".", "")), value: sums[i], pct: Math.round((sums[i] / max) * 100) }));
  }, [buckets.recebido]);
  const trend = useMemo(() => {
    if (chart.length < 2) return null;
    const cur = chart[chart.length - 1].value, prev = chart[chart.length - 2].value;
    if (prev <= 0) return null;
    return Math.round(((cur - prev) / prev) * 100);
  }, [chart]);

  // Movimentações por aba
  const movements = useMemo(() => {
    const recRows = (buckets.recebido || []).map((r) => ({ id: `r-${r.order_id || r.event_id}`, event_id: r.event_id, name: r.event_name, express: r.express, sub: "Entrada de evento", date: r.timeline?.recebido || r.paid_at || r.event_date, amount: r.cache, kind: "in" }));
    const paidOut = (fin?.contractor?.rows || []).filter((r) => r.paid).map((r, i) => ({ id: `po-${r.event_id}-${i}`, event_id: r.event_id, name: r.event_name, express: r.express, sub: `Repasse · ${r.musician}`, date: r.paid_at || r.date, amount: r.amount, kind: "out" }));
    const executados = [...recRows, ...paidOut].sort((a, b) => String(b.date || "").localeCompare(String(a.date || "")));

    const arReceber = (buckets.a_receber || []).map((r) => ({ id: `ar-${r.order_id || r.event_id}`, event_id: r.event_id, name: r.event_name, express: r.express, sub: r.note || r.status || "Aguardando repasse", date: r.event_date, amount: r.cache, kind: "pending" }));
    const problemas = (buckets.problemas || []).map((r) => ({ id: `pb-${r.order_id || r.event_id}`, event_id: r.event_id, name: r.event_name, express: r.express, sub: r.reason || "Pendência", date: r.event_date, amount: r.cache, kind: "problem" }));
    const toPay = (fin?.contractor?.rows || []).filter((r) => !r.paid).map((r, i) => ({ id: `tp-${r.event_id}-${i}`, event_id: r.event_id, name: r.event_name, express: r.express, sub: `A pagar · ${r.musician}`, date: r.date, amount: r.amount, kind: "out_pending" }));
    const pendentes = [...arReceber, ...problemas, ...toPay].sort((a, b) => String(b.date || "").localeCompare(String(a.date || "")));

    const previsto = (buckets.previsto || []).map((r) => ({ id: `pv-${r.event_id}`, event_id: r.event_id, name: r.event_name, express: r.express, sub: "Participação confirmada", date: r.event_date, amount: r.cache, kind: "future" }));
    const potencial = (buckets.potencial || []).map((r) => ({ id: `pt-${r.event_id}`, event_id: r.event_id, name: r.event_name, express: r.express, sub: "Candidatura em análise", date: r.event_date, amount: r.cache, kind: "future_soft" }));
    const futuros = [...previsto, ...potencial].sort((a, b) => String(a.date || "").localeCompare(String(b.date || "")));

    return { executados, pendentes, futuros };
  }, [buckets, fin]);

  const rows = movements[tab] || [];

  const doExport = async (fmt) => {
    if (!rows.length) { toast.info("Nada para exportar neste filtro."); return; }
    setExporting(fmt);
    const tid = toast.loading("Preparando arquivo...");
    try {
      const tabLabel = TABS.find((t) => t[0] === tab)[1];
      const columns = ["Período", "Data", "Evento", "Descrição", "Tipo", "Entrada (R$)", "Repasse (R$)", "Valor (R$)", "Status"];
      let totEnt = 0, totRep = 0;
      const erows = rows.map((m) => {
        const k = KIND_MAP[m.kind] || KIND_MAP.pending;
        const val = m.amount || 0;
        const isIn = k.dir === "in";
        if (isIn) totEnt += val; else totRep += val;
        const d = m.date ? new Date(String(m.date).includes("T") ? m.date : m.date + "T00:00:00").toLocaleDateString("pt-BR") : "";
        return [tabLabel, d, `${m.express ? "⚡ " : ""}${m.name || ""}`, m.sub || "", k.tipo, isIn ? money(val) : "", !isIn ? money(val) : "", money(val), k.status];
      });
      const totals = ["TOTAL", "", "", "", "", money(totEnt), money(totRep), money(totEnt - totRep), ""];
      const res = await api.post("/finance/export",
        { format: fmt, filename: `financeiro_${tab}`, title: `Financeiro — ${tabLabel} — Mí Chamou`, columns, rows: erows, totals },
        { responseType: "blob" });
      const ext = fmt === "xlsx" ? "xlsx" : fmt;
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `financeiro_${tab}.${ext}`; a.click(); URL.revokeObjectURL(url);
      toast.success("Exportação concluída.", { id: tid });
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Não foi possível gerar o arquivo. Tente novamente.", { id: tid }); }
    finally { setExporting(""); }
  };
  const canExport = hasFeature("export_excel") || hasFeature("export_csv") || hasFeature("export_pdf");

  return (
    <div className="max-w-3xl mx-auto space-y-6 pb-6" data-testid="payments-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="font-head text-3xl sm:text-4xl font-extrabold tracking-tight flex items-center gap-3"><Wallet className="h-8 w-8 text-accent" />Financeiro</h1>
          <p className="text-muted-foreground mt-1">Acompanhe seus ganhos, repasses e muito mais.</p>
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button data-testid="finance-export-btn" variant="outline" size="sm" aria-label="Exportar dados financeiros"
              className="h-9 mt-1 rounded-xl bg-transparent border-white/10 gap-1.5 shrink-0 text-muted-foreground hover:text-foreground">
              <Download className="h-4 w-4" />Exportar
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" data-testid="finance-export-menu" className="w-60 bg-[#121217] border-white/10">
            {canExport ? (
              <>
                {hasFeature("export_excel") && <ExportItem testid="export-xlsx-item" Icon={FileSpreadsheet} color="text-emerald-400" label="Excel" desc="Exportar planilha .xlsx" onSelect={() => doExport("xlsx")} />}
                {hasFeature("export_csv") && <ExportItem testid="export-csv-item" Icon={FileText} color="text-sky-400" label="CSV" desc="Exportar dados .csv" onSelect={() => doExport("csv")} />}
                {hasFeature("export_pdf") && <ExportItem testid="export-pdf-item" Icon={FileDown} color="text-rose-400" label="PDF" desc="Gerar relatório financeiro .pdf" onSelect={() => doExport("pdf")} />}
              </>
            ) : (
              <div className="px-3 py-3 text-xs text-muted-foreground" data-testid="finance-export-locked">Exportação (Excel, CSV e PDF) disponível em planos superiores.</div>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Banners Pix */}
      {payout && payout.status === "Não cadastrado" && (
        <div className="rounded-2xl border border-rose-500/40 bg-rose-500/10 p-4 flex items-center justify-between gap-3 flex-wrap" data-testid="pix-required-banner">
          <div className="min-w-0">
            <div className="font-semibold text-rose-400 flex items-center gap-2"><AlertTriangle className="h-4 w-4" />Cadastre sua chave Pix para receber</div>
            <p className="text-sm text-muted-foreground">Os cachês são repassados apenas via Pix. Sem uma chave cadastrada você não consegue receber.</p>
          </div>
          <Button data-testid="register-pix-btn" onClick={() => navigate("/perfil")} className="rounded-xl bg-rose-500 hover:bg-rose-600 text-white shrink-0">Cadastrar chave Pix</Button>
        </div>
      )}
      {pendingPix > 0 && (
        <div className="rounded-2xl border border-amber-500/40 bg-amber-500/10 p-4 flex items-center justify-between gap-3 flex-wrap" data-testid="pending-pix-banner">
          <div className="min-w-0 flex items-start gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-400 shrink-0 mt-0.5" />
            <div>
              <div className="font-semibold text-amber-400">Você tem {money(pendingPix)} aguardando o cadastro do seu Pix</div>
              <p className="text-sm text-muted-foreground">Assim que você cadastrar/corrigir sua chave Pix, o repasse é reprocessado automaticamente.</p>
            </div>
          </div>
          <Button data-testid="pending-pix-fix-btn" onClick={() => navigate("/perfil")} className="bg-amber-500 text-zinc-950 hover:bg-amber-400 rounded-xl shrink-0">Cadastrar/corrigir Pix</Button>
        </div>
      )}

      {/* Cartão de Saldo */}
      <div className="rounded-2xl border border-accent/40 bg-gradient-to-br from-accent/[0.08] to-transparent p-5 shadow-[0_0_28px_rgba(255,170,0,0.12)]" data-testid="balance-card">
        <div className="grid sm:grid-cols-2 gap-5">
          <div className="sm:border-r sm:border-white/10 sm:pr-5">
            <div className="flex items-center gap-2 text-muted-foreground text-sm"><Wallet className="h-4 w-4 text-accent" />Saldo líquido</div>
            <div className="font-head text-4xl font-extrabold mt-1 tracking-tight" data-testid="balance-value">{money(saldoLiquido)}</div>
            {trend != null && (
              <div className={`mt-1 flex items-center gap-1 text-sm font-semibold ${trend >= 0 ? "text-emerald-400" : "text-rose-400"}`}>
                {trend >= 0 ? <TrendingUp className="h-4 w-4" /> : <TrendingDown className="h-4 w-4" />}{trend >= 0 ? "+" : ""}{trend}%
                <span className="text-muted-foreground font-normal">em relação ao mês anterior</span>
              </div>
            )}
          </div>
          <div className="space-y-2.5">
            <MiniRow Icon={ArrowUp} color="text-emerald-400" bg="bg-emerald-500/15" label="Recebido" value={money(recebido)} testid="mini-recebido" />
            <MiniRow Icon={Clock} color="text-amber-400" bg="bg-amber-500/15" label="A receber" value={money(aReceber)} testid="mini-a-receber" />
            <MiniRow Icon={ArrowDown} color="text-rose-400" bg="bg-rose-500/15" label="Repasses" value={money(repasses)} testid="mini-repasses" />
            <MiniRow Icon={Users} color="text-emerald-400" bg="bg-emerald-500/15" label="Saldo líquido" value={money(saldoLiquido)} testid="mini-saldo-liquido" strong />
          </div>
        </div>
      </div>

      {/* Movimentações (gráfico) */}
      <div className="rounded-2xl border border-white/10 bg-card p-5" data-testid="chart-card">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-head text-lg font-bold flex items-center gap-2"><BarChart3 className="h-5 w-5 text-accent" />Movimentações</h2>
          <span className="text-xs text-muted-foreground border border-white/10 rounded-lg px-2.5 py-1">Últimos 6 meses</span>
        </div>
        <div className="flex items-end justify-between gap-2 h-44" data-testid="chart-bars">
          {chart.map((c, i) => {
            const last = i === chart.length - 1;
            return (
              <div key={i} className="flex-1 flex flex-col items-center justify-end gap-1 h-full">
                <span className={`text-[10px] font-mono font-semibold ${last ? "text-accent" : "text-muted-foreground"}`}>{c.value > 0 ? shortMoney(c.value) : ""}</span>
                <div className="w-full flex items-end justify-center h-full">
                  <div className={`w-6 sm:w-8 rounded-t-md transition-all ${last ? "bg-gradient-to-t from-[#FF4D00] to-accent" : "bg-gradient-to-t from-[#FF4D00]/70 to-[#FF7A29]/70"}`}
                    style={{ height: `${Math.max(c.pct, c.value > 0 ? 6 : 2)}%` }} />
                </div>
                <span className={`text-[11px] ${last ? "text-accent font-semibold" : "text-muted-foreground"}`}>{c.label}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Tabs */}
      <div className="grid grid-cols-3 gap-1 p-1 rounded-2xl bg-card border border-white/10" data-testid="movement-tabs">
        {TABS.map(([v, l]) => (
          <button key={v} data-testid={`tab-${v}`} onClick={() => setTab(v)}
            className={`py-2.5 rounded-xl text-sm font-semibold transition-all ${tab === v ? "bg-[#FF4D00] text-white shadow-[0_0_20px_rgba(255,79,0,0.35)]" : "text-muted-foreground hover:text-foreground"}`}>{l}</button>
        ))}
      </div>

      {/* Últimas movimentações */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-head text-lg font-bold">Últimas movimentações</h2>
        </div>
        {rows.length === 0 ? (
          <div className="rounded-2xl border border-white/10 bg-card p-8 text-center" data-testid="movements-empty">
            <Wallet className="h-8 w-8 text-muted-foreground/50 mx-auto mb-2" />
            <p className="text-sm text-muted-foreground">Nada em "{TABS.find((t) => t[0] === tab)[1]}" por enquanto.</p>
          </div>
        ) : (
          <div className="space-y-2.5" data-testid="movements-list">
            {rows.slice(0, 10).map((m) => <MovementRow key={m.id} m={m} onClick={() => m.event_id && navigate(`/eventos/${m.event_id}`)} />)}
          </div>
        )}
      </div>

      {/* Chave Pix */}
      <div className="rounded-2xl border border-white/10 bg-card p-4 flex items-center justify-between gap-3 flex-wrap" data-testid="payout-key-card">
        <div className="min-w-0">
          <div className="font-medium flex items-center gap-2">Recebimento por chave Pix
            {hasPix
              ? <span className="text-[10px] px-2 py-0.5 rounded-full border border-emerald-500/40 bg-emerald-500/15 text-emerald-400 font-semibold inline-flex items-center gap-1"><CheckCircle2 className="h-3 w-3" />Chave cadastrada</span>
              : <span className="text-[10px] px-2 py-0.5 rounded-full border border-rose-500/40 bg-rose-500/15 text-rose-400 font-semibold inline-flex items-center gap-1"><AlertTriangle className="h-3 w-3" />Obrigatório</span>}
          </div>
          <p className="text-sm text-muted-foreground">Seus cachês vão direto para a sua <b className="text-foreground">chave Pix</b> (qualquer banco). Edite no seu perfil.</p>
        </div>
        {hasPix
          ? <span className="flex items-center gap-1 text-emerald-400 font-medium shrink-0"><CheckCircle2 className="h-5 w-5" />{payout.masked}</span>
          : <Button data-testid="connect-pix-btn" onClick={() => navigate("/perfil")} className="bg-emerald-500 text-zinc-950 hover:bg-emerald-400 rounded-xl shrink-0">Cadastrar chave Pix</Button>}
      </div>

      {/* Histórico de repasses (detalhado) */}
      <MyPayouts />

      {/* Ajuda */}
      <div className="text-muted-foreground rounded-2xl border border-sky-500/25 bg-sky-500/[0.05] p-4 flex items-start gap-3 text-sm" data-testid="payments-help">
        <Info className="h-5 w-5 text-sky-400 shrink-0 mt-0.5" />
        <p>Para pagar a banda de um evento, abra o evento e use <b className="text-foreground">"Pagamento da Banda"</b>. O repasse a cada músico é liberado após check-in e check-out no show.</p>
      </div>
    </div>
  );
}

const MiniRow = ({ Icon, color, bg, label, value, testid, strong }) => (
  <div className="flex items-center gap-2.5" data-testid={testid}>
    <div className={`h-7 w-7 rounded-full ${bg} flex items-center justify-center shrink-0`}><Icon className={`h-4 w-4 ${color}`} /></div>
    <span className="text-sm text-muted-foreground flex-1">{label}</span>
    <span className={`font-mono font-bold ${strong ? "text-emerald-400" : "text-foreground"}`}>{value}</span>
  </div>
);

const MOV_STYLE = {
  in: { Icon: ArrowUp, color: "text-emerald-400", bg: "bg-emerald-500/15", amount: (v) => ({ cls: "text-emerald-400", txt: `+ ${money(v)}` }) },
  out: { Icon: ArrowDown, color: "text-rose-400", bg: "bg-rose-500/15", amount: (v) => ({ cls: "text-rose-400", txt: `- ${money(v)}` }) },
  pending: { Icon: Clock, color: "text-amber-400", bg: "bg-amber-500/15", amount: (v) => ({ cls: "text-amber-300", txt: money(v) }) },
  problem: { Icon: AlertTriangle, color: "text-rose-400", bg: "bg-rose-500/15", amount: (v) => ({ cls: "text-rose-300", txt: money(v) }) },
  out_pending: { Icon: ArrowDown, color: "text-amber-400", bg: "bg-amber-500/15", amount: (v) => ({ cls: "text-amber-300", txt: `- ${money(v)}` }) },
  future: { Icon: ArrowUpRight, color: "text-sky-400", bg: "bg-sky-500/15", amount: (v) => ({ cls: "text-sky-300", txt: money(v) }) },
  future_soft: { Icon: ArrowUpRight, color: "text-zinc-400", bg: "bg-zinc-500/15", amount: (v) => ({ cls: "text-muted-foreground", txt: money(v) }) },
};

const MovementRow = ({ m, onClick }) => {
  const s = MOV_STYLE[m.kind] || MOV_STYLE.pending;
  const amt = s.amount(m.amount || 0);
  return (
    <div data-testid={`movement-${m.id}`} onClick={onClick}
      className="flex items-center gap-3 rounded-2xl border border-white/10 bg-card p-3.5 cursor-pointer transition-all hover:border-accent/40">
      <div className={`h-10 w-10 rounded-full ${s.bg} flex items-center justify-center shrink-0`}><s.Icon className={`h-5 w-5 ${s.color}`} /></div>
      <div className="flex-1 min-w-0">
        <div className="font-semibold truncate flex items-center gap-1.5">
          {m.express && <span data-testid={`movement-express-${m.id}`} title="Em cima da Hora" className="inline-flex items-center gap-0.5 text-[9px] font-bold px-1 py-0.5 rounded bg-[#FF4D00]/15 text-[#FF4D00] border border-[#FF4D00]/30 shrink-0"><Zap className="h-2.5 w-2.5" />⚡</span>}
          <span className="truncate">{m.name}</span>
        </div>
        <div className="text-xs text-muted-foreground truncate">{m.sub}{m.date ? ` · ${fmtDate(m.date)}` : ""}</div>
      </div>
      <span className={`font-mono font-bold shrink-0 ${amt.cls}`}>{amt.txt}</span>
      <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
    </div>
  );
};

const ExportItem = ({ testid, Icon, color, label, desc, onSelect }) => (
  <DropdownMenuItem data-testid={testid} onSelect={onSelect} className="gap-3 py-2.5 cursor-pointer focus:bg-white/5">
    <Icon className={`h-5 w-5 ${color} shrink-0`} />
    <div className="min-w-0">
      <div className="text-sm font-medium text-foreground">{label}</div>
      <div className="text-xs text-muted-foreground">{desc}</div>
    </div>
  </DropdownMenuItem>
);
